import { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from '../../../../components'
import { FieldLabel } from '../../../Configurations/Coberturas/createCoverages/formPrimitives'
import { createXdock, updateXdock } from '../../../../services'

const T_PREFIX = 'lastmile_management.shipping_costs.xdock_form'

/**
 * Create / edit a cross-dock (the physical facility). Persists via the
 * POST/PATCH /lastmile/xdocks endpoints. `xdock` prefills edit mode. Country is read-only:
 * new cross-docks take `defaultCountry` (the shipping method's country); an existing one
 * keeps its own. The Xdock tab lists a single country and refetches that filtered key, so
 * letting the country be changed here would make the saved cross-dock silently vanish from
 * the table (in practice a listed xdock's country already matches the method's).
 */
export const XdockForm = ({ xdock, defaultCountry, onDone, t }) => {
  const isEdit = !!xdock
  const queryClient = useQueryClient()

  const [name, setName] = useState(xdock?.name || '')
  const [address, setAddress] = useState(xdock?.shipping_address || '')
  const [zipcode, setZipcode] = useState(xdock?.shipping_zipcode || '')
  const [phone, setPhone] = useState(xdock?.phone || '')
  // Country is fixed to the method's country (see the header note) — never user-selectable.
  const country = xdock?.country || defaultCountry || null

  const mutation = useMutation(payload => (isEdit ? updateXdock(xdock.id, payload) : createXdock(payload)), {
    onSuccess: () => {
      queryClient.invalidateQueries(['lastmile-xdocks'])
      onDone?.()
    },
  })

  const canSave = name.trim() && address.trim() && zipcode.trim() && country && !mutation.isLoading

  const handleSave = () => {
    const payload = {
      name: name.trim(),
      shipping_address: address.trim(),
      shipping_zipcode: zipcode.trim(),
      country,
    }
    if (phone.trim()) payload.phone = phone.trim()
    mutation.mutate(payload)
  }

  // Match the system's form field style (see SingleCoverageForm INPUT_CLS).
  const field = 'block w-full rounded-md border-gray-300 shadow-sm placeholder-gray-400 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'

  return (
    <div className="space-y-4">
      <label className="block">
        <FieldLabel required>{t(`${T_PREFIX}.name`)}</FieldLabel>
        <input type="text" className={field} value={name} onChange={e => setName(e.target.value)} />
      </label>
      <label className="block">
        <FieldLabel required>{t(`${T_PREFIX}.address`)}</FieldLabel>
        <input type="text" className={field} value={address} onChange={e => setAddress(e.target.value)} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <FieldLabel required>{t(`${T_PREFIX}.zipcode`)}</FieldLabel>
          <input type="text" className={field} value={zipcode} onChange={e => setZipcode(e.target.value)} />
        </label>
        <label className="block">
          <FieldLabel required>{t(`${T_PREFIX}.country`)}</FieldLabel>
          <input type="text" className={`${field} bg-gray-50 text-gray-500`} value={country || ''} disabled />
        </label>
      </div>
      <label className="block">
        <FieldLabel>{t(`${T_PREFIX}.phone`)}</FieldLabel>
        <input type="text" className={field} value={phone} onChange={e => setPhone(e.target.value)} />
      </label>

      {mutation.isError && <p className="text-sm text-red-600">{mutation.error?.response?.data?.error || t(`${T_PREFIX}.error`)}</p>}

      <div className="flex justify-end border-t border-gray-100 pt-4">
        <Button type="primary" disabled={!canSave} onClick={handleSave}>
          {mutation.isLoading ? t(`${T_PREFIX}.saving`) : t(`${T_PREFIX}.save`)}
        </Button>
      </div>
    </div>
  )
}
